import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';

import { Wechat_ErrCode, WechatServices } from '@quan/proto-api-client';

import { persistUserStore, StoreName } from '@/stores';
import userStore from '@/stores/user';

import { debounce, OauthClient } from '@planjs/utils';
import { useCreation, useBoolean } from '@planjs/react-utils';
import Device from '@/utils/device';
import { Button, Modal, Toast } from 'antd-mobile';
import styles from './index.module.less';

export enum Scope {
  snsapiBase = 'snsapi_base', // 静默授权
  snsapiUserinfo = 'snsapi_userinfo', // 弹出授权
}

export const AppSessionKey = `${process.env.PKGName}-${process.env.mode}-app-name`;
export const NeedAuthSessionKey = `${process.env.PKGName}-${process.env.mode}-need-auth-2`;

export enum AppName {
  LBS = 'LBS',
}

export enum NeedAuthState {
  Need = 'NEED',
  Unneed = 'UNNEED',
}

interface Props extends PropsWithChildren<{}> {
  appid: string; // 微信服务号的appid（不能用订阅号）
  scope?: Scope;
  isLbs?: boolean;
  // 忽略unionid 可以不用授权
  ignoreUnionId?: boolean;
}

const APPID_CACHE_KEY = `quan-h5-${process.env.mode}-wxappid`;

function WxLogin(props: Props) {
  const { appid, scope = Scope.snsapiUserinfo, isLbs, ignoreUnionId = false } = props;
  const {
    state: fetchAuthUrlLoading,
    setTrue: startFetchAuthUrl,
    setFalse: endFetchAuthUrl,
  } = useBoolean(false);
  const {
    state: fetchLoginLoading,
    setTrue: startFetchLogin,
    setFalse: endFetchLogin,
  } = useBoolean(false);

  const [visible, setVisible] = React.useState<boolean>(true);

  const oauthClient = useCreation(
    () =>
      new OauthClient({
        consumerKeys: ['code', 'appid', 'state'],
      }),
    [],
  );

  const authParams = React.useMemo(() => oauthClient.getAuthParams(), [oauthClient]);

  const wxLogin = React.useCallback(async () => {
    try {
      startFetchLogin();
      await userStore!.LoginAsync({
        code: authParams!.code,
        wx_app_id: appid,
      });
      window.localStorage.setItem(APPID_CACHE_KEY, appid);
    } catch (error: any) {
      // TODO 错误处理
      // history.replace({
      //   pathname: PageRouteEnum.ErrorEnvironment,
      //   search: `err=${ErrType.SYSTEM_ERROR}`,
      // });
      console.log(error);
      if (error && error.errcode === Wechat_ErrCode.ErrWechat) {
        // Toast.show({
        //   content: <div style={{ fontSize: 16 }}>授权失败，请重新授权！</div>,
        // });
        Modal.show({
          content: <div style={{ fontSize: 16, textAlign: 'center' }}>授权失败，请重新授权！</div>,
          actions: [
            {
              key: '1',
              text: <div style={{ fontSize: 16 }}>知道了</div>,
              onClick: () => {
                window.location.reload();
              },
            },
          ],
        });
        setVisible(true);
      }
      return Promise.reject(error);
    } finally {
      endFetchLogin();
      // TODO 干掉参数重新获取
      // window.location.replace(oauthClient.originalUrl);
      window.history.replaceState(authParams, '', oauthClient.originalUrl);
    }
  }, [appid, authParams, oauthClient.originalUrl]);

  const doWXLogin = React.useCallback(() => {
    if (!appid) return;

    startFetchAuthUrl();

    WechatServices.GetOauthUrl({
      wx_app_id: appid,
      component_key: 'quan',
      state: 'STATE',
      redirect_uri: oauthClient.getOriginalUrl(window.location.href),
      scope,
    })
      .then((res = { url: '' }) => {
        if (res.url && isLbs) {
          window.location.href = res.url;
          if (Device.isAndroid) window.location.reload();
        } else if (res.url) {
          window.location.replace(res.url);
        }
      })
      .catch((e: any) => {
        // TODO
        console.log(e);
      })
      .finally(() => {
        endFetchAuthUrl();
        window.localStorage.setItem(APPID_CACHE_KEY, appid);
      });
  }, [appid, oauthClient, scope, isLbs]);

  const login = debounce(
    React.useCallback(() => {
      if (!appid) return;

      if (!WxLogin.checkIsCurrentAppId(appid)) {
        userStore?.LoginOutAsync();
        doWXLogin();
      } else if (authParams) {
        wxLogin().catch((e) => e);
      } else {
        persistUserStore.then((userStore) => {
          if (!userStore.loginResp) {
            // 针对特殊需求，不强制跳转授权
            doWXLogin();
          }
        });
      }
    }, [appid, authParams, doWXLogin, oauthClient, wxLogin]),
    200,
  );

  useEffect(() => {
    if (!appid) return;

    if (authParams) {
      setVisible(false);
      wxLogin()
        .then(() => {
          setVisible(true);
        })
        .catch((e) => e);
      return;
    }

    if (
      (userStore?.union_id || ignoreUnionId) &&
      !userStore?.loginResp &&
      WxLogin.checkIsCurrentAppId(appid)
    ) {
      //自动授权
      doWXLogin();
    }
  }, []);

  const isNeedAuth =
    userStore?.loginResp?.is_snapshotuser === 1 ||
    !((userStore?.union_id || ignoreUnionId || !visible) && WxLogin.checkIsCurrentAppId(appid));

  return (
    <>
      <div
        className={styles['wx_login']}
        style={{
          display:
            // 是快照页一定要展示授权页
            // 没有union_id或切换了公众号则展示授权页
            isNeedAuth ? 'block' : 'none',
        }}
      >
        <div className={styles['mask']}>
          <div className={styles['mask_title']}>参与活动须知</div>
          <div className={styles['mask_desc']}>本活动需要授权登录后才可参与</div>
          <Button
            color="primary"
            className={styles['mask_btn']}
            onClick={() => {
              login();
            }}
          >
            授权登录
          </Button>
        </div>
      </div>

      {!isNeedAuth ? props.children : null}
    </>
  );
}

WxLogin.APPID_CACHE_KEY = APPID_CACHE_KEY;
WxLogin.checkIsCurrentAppId = function (appid: string) {
  const cached = window.localStorage.getItem(APPID_CACHE_KEY);
  return cached && appid && cached === appid;
};

export default observer(WxLogin);
